import {CoworkBarePanel} from '../admin/CoworkFormPanel';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Personal, fetchPersonalById, updatePersonal } from '../../services/personalService';
import ImageUpload from '../Upload/ImageUpload'; // Ajusta la ruta según tu proyecto
import { useModalFocus } from '../../hooks/useModalFocus';
import { useModalLifecycle } from '../../hooks/useModalLifecycle';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    person: Personal | null;
    onUpdate: (updated: Personal) => void;
}

/**
 * Estado del PIN vigente cargado desde el detalle (solo el administrador lo
 * recibe): ``visible`` trae el valor en claro; ``legacy`` hay PIN pero solo
 * como hash (anterior al cifrado) y no puede mostrarse; ``none`` no hay PIN;
 * ``error`` no se pudo consultar. En todos los casos se puede fijar uno nuevo.
 */
type PinStatus = 'loading' | 'visible' | 'legacy' | 'none' | 'error';

/** Estado visual de UNA identidad (apertura + persona), etiquetado con su clave. */
interface View {
    key: number;
    cargo: string;
    telefono: string;
    foto: File | null;
    pinCaja: string;
    pinStatus: PinStatus;
    currentPin: string | null;
    loading: boolean;
    error: string;
}

const freshView = (key: number, person: Personal | null): View => ({
    key,
    cargo: person?.cargo || '',
    telefono: person?.telefono || '',
    foto: null,
    pinCaja: '',
    pinStatus: 'loading',
    currentPin: null,
    loading: false,
    error: '',
});

const TITLE_ID = 'edit-personnel-title';

/**
 * Edición de personal (cargo, teléfono, foto y PIN de caja).
 *
 * Identidad = apertura + persona (``isOpen`` y ``person.id``), fijada EN
 * RENDER por ``useModalLifecycle``: en el mismo render que trae a otra
 * persona (o el cierre/reapertura) se invalida la generación y se apagan los
 * handlers, así que ni un submit en la frontera del commit ni una respuesta
 * tardía (detalle o guardado) de la persona anterior pueden enviar sus datos
 * al id nuevo, cerrar, entregar ``onUpdate`` ni marcar error/loading. El
 * estado visual se lee DERIVADO por clave: el formulario de A nunca se pinta
 * como si fuera de B, ni siquiera en el commit anterior a los efectos.
 *
 * Cerrar (botón, Cancelar, backdrop o Escape) es terminal e idempotente:
 * avisa al padre una sola vez y, aunque tarde en retirar el modal del DOM,
 * ningún submit vuelve a enviar hasta la siguiente apertura válida.
 *
 * Modal accesible: ``role="dialog"`` con ``aria-modal`` y título asociado;
 * al abrir mueve el foco al campo Cargo; Escape cierra; Tab/Shift+Tab quedan
 * confinados; al cerrar o desmontar devuelve el foco al opener si sigue en el DOM.
 */
const EditPersonnelModal: React.FC<Props> = ({ isOpen, onClose, person, onUpdate }) => {
    const [viewState, setViewState] = useState<View>(() => freshView(0, null));
    // El administrador ya escribió un PIN durante la carga: el detalle que
    // llegue después actualiza el PIN vigente pero NO pisa lo escrito.
    const pinDirtyRef = useRef(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const cargoInputRef = useRef<HTMLInputElement>(null);

    const lifecycle = useModalLifecycle({
        identity: isOpen && person ? person.id : null,
        onClose,
        onReset: key => { pinDirtyRef.current = false; setViewState(freshView(key, person)); },
    });
    const { identityKey, rendered } = lifecycle;
    // Estado derivado: solo cuenta el de la identidad vigente.
    const view = viewState.key === identityKey ? viewState : freshView(identityKey, person);
    const patch = (changes: Partial<Omit<View, 'key'>>) => setViewState(prev => ({
        ...(prev.key === identityKey ? prev : freshView(identityKey, person)),
        ...changes,
        key: identityKey,
    }));

    // El listado nunca trae el PIN: se consulta el detalle al abrir. Layout:
    // arranca en el mismo commit que el reset, con la generación de ESTA identidad.
    useLayoutEffect(() => {
        if (!rendered || !person) return;
        const generation = lifecycle.invalidate();
        fetchPersonalById(person.id)
            .then(detail => {
                if (!lifecycle.isLive(generation)) return;
                const visible = detail.pin_caja_visible ?? null;
                patch({
                    currentPin: visible,
                    pinStatus: visible ? 'visible' : detail.pin_caja_configurado ? 'legacy' : 'none',
                    ...(pinDirtyRef.current ? {} : { pinCaja: visible ?? '' }),
                });
            })
            .catch(() => { if (lifecycle.isLive(generation)) patch({ pinStatus: 'error' }); });
        // La clave de identidad ya codifica isOpen y person.id; el resto son refs/estables.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityKey]);

    useModalFocus({ active: rendered, panelRef, initialFocusRef: cargoInputRef, onEscape: lifecycle.close });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        patch({ [e.target.name]: e.target.value } as Partial<View>);
    };

    const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        pinDirtyRef.current = true;
        patch({ pinCaja: e.target.value.replace(/\D/g, '').slice(0, 6) });
    };

    // Solo se reenvía el PIN si el administrador escribió uno DISTINTO al
    // vigente: guardar sin tocarlo no cambia el hash ni revoca los grants.
    const pinChanged = view.pinCaja !== '' && view.pinCaja !== (view.currentPin ?? '');

    const pinHelp = (() => {
        switch (view.pinStatus) {
            case 'loading': return 'Consultando el PIN vigente…';
            case 'visible': return 'PIN vigente. Cámbialo aquí para reemplazarlo; vacío lo conserva.';
            case 'legacy': return 'Hay un PIN configurado pero no puede mostrarse (fue registrado antes del cifrado). Escribe uno nuevo para reemplazarlo.';
            case 'none': return 'Sin PIN configurado. Escribe uno de 3 a 6 dígitos para habilitarlo.';
            default: return 'No se pudo cargar el PIN vigente. Puedes escribir uno nuevo o dejarlo vacío para conservarlo.';
        }
    })();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Cerrado, en la frontera de un cambio de identidad o guardando: no se envía.
        if (!person || !lifecycle.canWrite()) return;
        if (pinChanged && !/^\d{3,6}$/.test(view.pinCaja)) {
            patch({ error: 'El PIN de caja debe tener de 3 a 6 dígitos.' });
            return;
        }

        // Bloqueo síncrono + generación vigente (sin invalidar: un detalle aún
        // en vuelo de ESTA apertura sigue siendo válido) y persona editada.
        const generation = lifecycle.beginWrite();
        if (generation === null) return;
        const personId = person.id;
        patch({ loading: true, error: '' });

        try {
            const updated = await updatePersonal(personId, {
                cargo: view.cargo,
                telefono: view.telefono,
                foto: view.foto || undefined,
                pin_caja: pinChanged ? view.pinCaja : undefined,
            });
            if (!lifecycle.isLive(generation)) return;
            // Se libera el guardado ANTES de cerrar (close invalida la generación
            // y el finally ya no aplicaría): si el padre tardara en desmontar, el
            // modal no queda bloqueado en "Actualizando...".
            lifecycle.endWrite(generation);
            patch({ loading: false });
            onUpdate(updated);
            lifecycle.close();
        } catch {
            // Sin console.error: el AxiosError arrastra el FormData enviado
            // (incluido el PIN) y la respuesta; nada de eso va a la consola.
            if (lifecycle.isLive(generation)) patch({ error: 'Error al actualizar el personal' });
        } finally {
            if (lifecycle.isLive(generation)) {
                lifecycle.endWrite(generation);
                patch({ loading: false });
            }
        }
    };

    if (!rendered || !person) return null;

    return (
        <div data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-personnel-overlay">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" data-testid="edit-personnel-backdrop" onClick={lifecycle.close} />
            <CoworkBarePanel data-luma-dialog="true"
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
                className="luma-personnel-dialog bg-white p-4 sm:p-6 rounded-xl w-full max-w-2xl min-w-0 border border-gray-100 shadow-xl relative overflow-y-auto max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] [overflow-wrap:anywhere] focus:outline-none"
            >
                <div className="flex justify-between items-start gap-3 mb-4">
                    <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900">Editar Personal</h2>
                    <div className="flex items-center gap-4 min-w-0">
                        {view.error && <div className="text-red-500 text-sm" role="alert">{view.error}</div>}
                        <button
                            type="button"
                            onClick={lifecycle.close}
                            aria-label="Cerrar edición de personal"
                            className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="space-y-4 mb-4">
                        {/* Email (solo lectura) */}
                        <div>
                            <label htmlFor="edit-personnel-email" className="block text-sm font-medium text-gray-700 mb-2">
                                Correo electrónico
                            </label>
                            <input
                                id="edit-personnel-email"
                                type="email"
                                name="email"
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                                value={person.usuario?.email || 'N/A'}
                            />
                        </div>

                        {/* Cargo */}
                        <div>
                            <label htmlFor="edit-personnel-cargo" className="block text-sm font-medium text-gray-700 mb-2">
                                Cargo
                            </label>
                            <input
                                id="edit-personnel-cargo"
                                ref={cargoInputRef}
                                name="cargo"
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={view.cargo}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Teléfono */}
                        <div>
                            <label htmlFor="edit-personnel-telefono" className="block text-sm font-medium text-gray-700 mb-2">
                                Teléfono
                            </label>
                            <input
                                id="edit-personnel-telefono"
                                name="telefono"
                                type="tel"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={view.telefono}
                                onChange={handleChange}
                            />
                        </div>

                        {/* PIN de caja: visible para el administrador (detalle), editable */}
                        <div>
                            <label htmlFor="edit-pin-caja" className="block text-sm font-medium text-gray-700 mb-2">PIN de caja</label>
                            <input id="edit-pin-caja" type="text" inputMode="numeric" autoComplete="off" minLength={3} maxLength={6} pattern="[0-9]{3,6}" value={view.pinCaja} onChange={handlePinChange} placeholder="3 a 6 dígitos" aria-describedby="edit-pin-caja-help" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 tracking-widest" />
                            <p id="edit-pin-caja-help" className="text-xs text-gray-500 mt-1">{pinHelp}</p>
                        </div>

                        {/* Foto de perfil (opcional) - con currentImage; se remonta por identidad
                            para que la vista previa de otra persona no se conserve. */}
                        <div>
                            <ImageUpload
                                key={identityKey}
                                setImagen={(file) => patch({ foto: file })}
                                currentImage={person.foto_url ?? undefined}
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-3 mt-6" data-testid="edit-personnel-actions">
                        <button
                            type="button"
                            onClick={lifecycle.close}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={view.loading}
                            className={`px-4 py-2 text-sm bg-[#3564db] text-white rounded-lg transition-colors font-medium flex items-center ${view.loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8f1f64]'
                                }`}
                        >
                            {view.loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Guardar Cambios'
                            )}
                        </button>
                    </div>
                </form>
            </CoworkBarePanel>
        </div>
    );
};

export default EditPersonnelModal;


